import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { randomCharacterSeed } from '../util/characterPresets';
import { useSelectionStore } from './selectionStore';
let characterInsertCounter = 0;
/** Bu araçlara geçildiğinde sahne nesnesi seçimi kalkar; çizim hedefi `currentLayer` olur. */
const TOOLS_THAT_CLEAR_SCENE_SELECTION = [
    'line',
    'rectangle',
    'circle',
    'triangle',
    'curve',
    'polygon',
    'text',
];
export const useEditorStore = create()(persist((set) => ({
    // Initial state
    mode: 'edit',
    aiChatVisible: true,
    aiChatText: '',
    aiChatWidth: 500,
    aiChatHistory: [],
    aiChatMovieId: null,
    aiChatContext: null,
    aiChatConversations: [],
    aiChatActiveConversationId: null,
    aiChatDebugMode: false,
    aiSamplePrompts: [
        "Create a scene with a man and a woman walking in a park.",
        "A rainy street, with cars passing slowly. Storefronts are open. A man is walking alone, looking for a place to eat.",
        "A sunny beach, with a few people swimming and sunbathing. A few boats are anchored in the water. A man is walking on the beach, looking for a place to eat.",
    ],
    currentTool: 'select',
    currentFrame: 0,
    fps: 24,
    isPlaying: false,
    clipboard: null,
    showGrid: false,
    snapToGrid: false,
    gridSize: 10,
    zoom: 1,
    fillColor: '#3b82f6',
    strokeColor: '#000000',
    strokeWidth: 2,
    strokeStyle: 'solid',
    sceneEffects: [],
    pendingCharacterInsert: null,
    // Actions
    setMode: (mode) => set({ mode }),
    setAiChatVisible: (visible) => set({ aiChatVisible: visible }),
    setAiChatText: (text) => set({ aiChatText: text }),
    setAiChatWidth: (width) => set({ aiChatWidth: Math.max(200, Math.min(800, width)) }),
    setAiChatHistory: (history, movieId) => set((state) => ({
        aiChatHistory: history,
        aiChatMovieId: movieId !== undefined ? movieId : state.aiChatMovieId,
    })),
    addAiChatMessage: (message) => set((state) => ({
        aiChatHistory: [...state.aiChatHistory, message]
    })),
    removeAiChatMessage: (id) => set((state) => ({
        aiChatHistory: state.aiChatHistory.filter((m) => m.id !== id),
    })),
    setAiChatContext: (context) => set({ aiChatContext: context }),
    setAiChatMovieId: (movieId) => set({ aiChatMovieId: movieId }),
    setAiChatConversations: (conversations) => set({ aiChatConversations: conversations }),
    setAiChatActiveConversationId: (id) => set({ aiChatActiveConversationId: id }),
    addAiChatConversation: (conversation) => set((state) => ({
        aiChatConversations: [conversation, ...state.aiChatConversations],
    })),
    setAiChatDebugMode: (enabled) => set({ aiChatDebugMode: enabled }),
    toggleAiChatDebugMode: () => set((state) => ({ aiChatDebugMode: !state.aiChatDebugMode })),
    removeAiChatConversation: (id) => set((state) => {
        const filtered = state.aiChatConversations.filter(c => c.id !== id);
        const isActive = state.aiChatActiveConversationId === id;
        return {
            aiChatConversations: filtered,
            // If we removed the active tab, select the first remaining or null
            aiChatActiveConversationId: isActive
                ? (filtered.length > 0 ? filtered[0].id : null)
                : state.aiChatActiveConversationId,
            // Clear history if active tab was removed
            ...(isActive ? { aiChatHistory: [] } : {}),
        };
    }),
    resetAiChat: () => set({
        aiChatText: '',
        aiChatHistory: [],
        aiChatMovieId: null,
        aiChatContext: null,
        aiChatConversations: [],
        aiChatActiveConversationId: null,
    }),
    setAiSamplePrompts: (prompts) => set({ aiSamplePrompts: prompts }),
    setTool: (tool) => {
        if (TOOLS_THAT_CLEAR_SCENE_SELECTION.includes(tool)) {
            useSelectionStore.getState().clearSelection();
        }
        set({ currentTool: tool });
    },
    setFrame: (frame) => set({ currentFrame: Math.max(0, frame) }),
    setFps: (fps) => set({ fps: Math.max(1, Math.min(60, fps)) }),
    play: () => set({ isPlaying: true }),
    pause: () => set({ isPlaying: false }),
    stop: () => set({ isPlaying: false, currentFrame: 0 }),
    togglePlay: () => set((state) => ({ isPlaying: !state.isPlaying })),
    nextFrame: () => set((state) => ({ currentFrame: state.currentFrame + 1 })),
    prevFrame: () => set((state) => ({ currentFrame: Math.max(0, state.currentFrame - 1) })),
    setClipboard: (item) => set({ clipboard: item }),
    setShowGrid: (show) => set({ showGrid: show }),
    setSnapToGrid: (snap) => set({ snapToGrid: snap }),
    setGridSize: (size) => set({ gridSize: Math.max(1, size) }),
    setZoom: (zoom) => set({ zoom: Math.max(0.1, Math.min(4, zoom)) }),
    zoomIn: () => set((state) => ({ zoom: Math.min(4, state.zoom * 1.25) })),
    zoomOut: () => set((state) => ({ zoom: Math.max(0.1, state.zoom / 1.25) })),
    resetZoom: () => set({ zoom: 1 }),
    setFillColor: (color) => set({ fillColor: color }),
    setStrokeColor: (color) => set({ strokeColor: color }),
    setStrokeWidth: (width) => set({ strokeWidth: Math.max(0, Math.min(40, width)) }),
    setStrokeStyle: (style) => set({ strokeStyle: style }),
    setSceneEffects: (effects) => set({ sceneEffects: effects }),
    upsertSceneEffect: (effect) => set((state) => {
        const existingIndex = state.sceneEffects.findIndex((item) => item.id === effect.id || item.effectType === effect.effectType);
        if (existingIndex === -1) {
            return { sceneEffects: [...state.sceneEffects, effect] };
        }
        const next = [...state.sceneEffects];
        next[existingIndex] = effect;
        return { sceneEffects: next };
    }),
    removeSceneEffect: (effectId) => set((state) => ({
        sceneEffects: state.sceneEffects.filter((effect) => effect.id !== effectId),
    })),
    requestCharacterInsert: (preset) => set({
        currentTool: 'select',
        pendingCharacterInsert: {
            preset,
            seed: randomCharacterSeed(),
            nonce: ++characterInsertCounter,
        },
    }),
    clearCharacterInsert: () => set({ pendingCharacterInsert: null }),
}), {
    name: 'prototype-editor',
    partialize: (state) => ({
        aiChatHistory: state.aiChatHistory,
        aiChatMovieId: state.aiChatMovieId,
        aiChatContext: state.aiChatContext,
        aiChatWidth: state.aiChatWidth,
        aiChatConversations: state.aiChatConversations,
        aiChatActiveConversationId: state.aiChatActiveConversationId,
        aiChatDebugMode: state.aiChatDebugMode,
        fillColor: state.fillColor,
        strokeColor: state.strokeColor,
        strokeWidth: state.strokeWidth,
        strokeStyle: state.strokeStyle,
    }),
}));
